import * as React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

interface CheckboxOption {
    label: string;
    value: string;
}

interface InputCheckboxGroupProps {
    name: string;
    options: CheckboxOption[];
    direction?: 'row' | 'column';
    onChange?: (value: Record<string, boolean>) => void;
}

export default function InputCheckboxGroup({
    name,
    options,
    direction = 'column',
    onChange,
}: InputCheckboxGroupProps) {
    const { control, setValue, watch } = useFormContext();
    const formValue = watch(name);

    const [selectedValues, setSelectedValues] = React.useState<Record<string, boolean>>({});

    // Update local state when form value changes
    React.useEffect(() => {
        if (formValue && typeof formValue === 'object') {
            setSelectedValues(formValue);
        } else {
            // Initialize with all options set to false
            const initialValues: Record<string, boolean> = {};
            options.forEach(option => {
                initialValues[option.value] = false;
            });
            setSelectedValues(initialValues);
        }
    }, [formValue, options]);

    const handleCheckboxChange = (optionValue: string, checked: boolean) => {
        const newValues = {
            ...selectedValues,
            [optionValue]: checked
        };

        setSelectedValues(newValues);
        setValue(name, newValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        onChange?.(newValues);
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormGroup sx={{ flexDirection: direction }}>
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            control={
                                <Checkbox
                                    checked={selectedValues[option.value] || false}
                                    onChange={(event) => handleCheckboxChange(option.value, event.target.checked)}
                                />
                            }
                            label={option.label}
                        />
                    ))}
                </FormGroup>
            )}
        />
    );
}
